import { useState, useEffect, useCallback } from 'react';
import { useSettingsStore } from '../store/settings';
import { useCreditsStore } from '../store/credits';
import { TIER_INFO, TIER_CREDITS, POLAR_PRODUCT_IDS, type SubscriptionTier } from '@n8n-builder/shared';
import { API_BASE_URL } from '../lib/api-config';

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

const TIER_FEATURES: Record<SubscriptionTier, string[]> = {
  free: ['Basic workflow generation', 'Community support'],
  pro: [
    'Unlimited workflows',
    '811+ n8n node support',
    'Plan, Build, and Edit modes',
    'Direct deployment to your n8n instance',
    'Import your existing workflows',
    'MCP access',
    'Workflow edit mode',
    'Email support',
  ],
  business: [
    'Everything in Pro, plus:',
    'Unlimited conversation history',
    'Priority processing',
    'Use the latest Opus model',
    'Connect multiple N8N instances',
    'Priority support',
  ],
};

export function UpgradeModal() {
  const { isUpgradeModalOpen, closeUpgradeModal } = useSettingsStore();
  const { credits } = useCreditsStore();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeUpgradeModal();
      }
    },
    [closeUpgradeModal]
  );

  useEffect(() => {
    if (isUpgradeModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isUpgradeModalOpen, handleKeyDown]);

  useEffect(() => {
    if (!isUpgradeModalOpen) {
      setError(null);
      setCheckoutLoading(null);
    }
  }, [isUpgradeModalOpen]);

  if (!isUpgradeModalOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeUpgradeModal();
    }
  };

  const handleUpgrade = async (tier: 'pro' | 'business') => {
    setCheckoutLoading(tier);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId: POLAR_PRODUCT_IDS[tier] }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const { url } = await res.json();

      closeUpgradeModal();
      const { PolarEmbedCheckout } = await import('@polar-sh/checkout/embed');
      PolarEmbedCheckout.create(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const tiers = ['pro', 'business'] as const;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden animate-fade-in">
        {/* Close button */}
        <button
          onClick={closeUpgradeModal}
          className="absolute top-5 right-5 z-10 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900">You've run out of credits</h2>
            <p className="text-base text-gray-500 mt-2">
              You have <span className="font-medium text-gray-700">{credits}</span> credits remaining. Upgrade to keep building.
            </p>
          </div>

          {error && (
            <div className="border border-red-200 rounded-xl p-3 bg-red-50 mb-5">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Plan cards */}
          <div className="grid grid-cols-2 gap-5">
            {tiers.map((tier) => {
              const info = TIER_INFO[tier];
              const features = TIER_FEATURES[tier];

              return (
                <div
                  key={tier}
                  className="border border-gray-200 rounded-xl p-6 flex flex-col"
                >
                  <div className="mb-5">
                    <h4 className="text-lg font-semibold text-gray-900">{info.name}</h4>
                    <div className="mt-1">
                      <span className="text-3xl font-bold text-gray-900">${info.price}</span>
                      <span className="text-sm text-gray-500">/mo</span>
                    </div>
                  </div>

                  <div className="mb-4 pb-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{TIER_CREDITS[tier]} credits/month</p>
                  </div>

                  <ul className="space-y-2.5 mb-7 flex-1">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(tier)}
                    disabled={checkoutLoading !== null}
                    className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkoutLoading === tier ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Loading...
                      </span>
                    ) : (
                      `Upgrade to ${info.name}`
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
